// MIDAS V3 — <CondInfoV3> condition explanation on a Variant E status-table
// condition cell (MIDAS-ANSWER-FORMAT-1 round 6 §(e); spec
// IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/MIDAS_ANSWER_FORMAT_R6_SPEC.md §4.4;
// Security review 2026-10-06, SEC-REQ-CI-3 / CI-4 / CI-5 / CI-8).
//
//   | ✕ | Pripravenosť regulátora 611D <cond-info text="…"/> | … |
//         └──────── trigger (dotted underline + "i") ────────┘
//
// Reached ONLY from StatusCellV3 for a BODY condition cell of an annotated
// status table (SEC-REQ-CI-5 placement gate) — everywhere else the sentinel
// renders as visible plain text (markdownComponentsV3). One interactive
// element per cell: the condition text itself is the trigger.
//
//   desktop (≥ 1280 px, useBreakpoint)  hover / keyboard-focus TOOLTIP — the
//       shared Radix tooltip every V3 chip uses, same ink info box as the
//       live-row / src-ref chips (SrcInfoBoxV3). Not portaled → V3 tokens
//       resolve and the fixed popper is not clipped by .midas-st__scroll.
//   phone + tablet (< 1280 px)  tap / click POPOVER (Radix Tooltip is
//       hover-only by design). Dismiss: outside tap + Esc (Radix defaults) and
//       any scroll (window capture listener below — the chat list scrolls an
//       element, and element scroll does not bubble). Known gap: a touch-only
//       device ≥ 1280 px gets the hover tooltip (width-tiered on purpose).
//
// The explanation is a React TEXT child only (SEC-REQ-CI-3 / SEC-REQ-5): no
// dangerouslySetInnerHTML, no markdown pass, no attribute, no title, no
// data-* copy, no logging (SEC-REQ-CI-8). The bare `data-cond-info=""` marker
// carries nothing.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Styles: ./cond-info.css.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { Tooltip, TooltipTrigger } from "../v2/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../v2/ui/popover";
import { resolveV3PaletteClass } from "../../midasV3Palette";
import { SrcInfoBoxBodyV3, SrcInfoBoxV3 } from "./SrcRefV3";

/** SEC-REQ-CI-4: the explanation box caps at ~320 px and wraps any token. */
export const COND_INFO_BOX_MAX_WIDTH = 320;

/** Page-menu tier of the V3 z-index ladder (1290 page menu / 1300 dialog /
 *  1400 dialog-borne menu / 1500 toast): the status table is a page surface.
 *  If a status table is ever hosted inside a dialog, this moves to 1400
 *  (ModelPickerV3 / RailRowMenuV3 precedent). */
export const COND_INFO_POPOVER_Z = 1290;

const isBlank = (node: React.ReactNode): boolean =>
  node == null ||
  typeof node === "boolean" ||
  (typeof node === "string" && node.trim() === "") ||
  (Array.isArray(node) && node.every(isBlank));

export interface CondInfoV3Props {
  /** The decoded, validated explanation (condInfoGrammar). Text only. */
  text: string;
  /** The condition prose — the trigger's visible label. Absent / blank →
   *  glyph-only trigger named by `condInfo.aria`. */
  children?: React.ReactNode;
}

export const CondInfoV3: React.FC<CondInfoV3Props> = ({ text, children }) => {
  const { translate } = useTranslation("chatBot");
  const { isDesktop } = useBreakpoint();
  const [open, setOpen] = React.useState(false);
  // A scroll-close must not hand focus back to the trigger: focus() would
  // scroll the page straight back to the row the user is scrolling away from.
  const closedByScrollRef = React.useRef(false);

  const eyebrow = translate("condInfo.eyebrow", {
    defaultValue: "Explanation",
  }) as string;
  const glyphOnly = isBlank(children);
  const ariaLabel = glyphOnly
    ? (translate("condInfo.aria", { defaultValue: "Show explanation" }) as string)
    : undefined;

  // The tier picks the primitive; a tier change swaps it, so start closed.
  React.useEffect(() => {
    setOpen(false);
  }, [isDesktop]);

  // Phone / tablet popover: any scroll while open dismisses it.
  React.useEffect(() => {
    if (!open) return undefined;
    closedByScrollRef.current = false;
    const close = () => {
      closedByScrollRef.current = true;
      setOpen(false);
    };
    window.addEventListener("scroll", close, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", close, { capture: true });
  }, [open]);

  const trigger = (
    <button type="button" className="cond-info" aria-label={ariaLabel} data-cond-info="">
      {!glyphOnly && children}
      {/* Screen readers hear "<condition>, Explanation, button". */}
      {!glyphOnly && <span className="cond-info__sr">, {eyebrow}</span>}
      <span className="cond-info__i" aria-hidden="true">
        i
      </span>
    </button>
  );

  if (isDesktop) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        {/* Inherits the primitive's z-50 — parity with every sibling V3 chip
            tooltip (live-row, src-ref, live-ref), which share this scroll body. */}
        <SrcInfoBoxV3
          eyebrow={eyebrow}
          body={text}
          maxWidth={COND_INFO_BOX_MAX_WIDTH}
          wrapAnywhere
        />
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        collisionPadding={8}
        aria-label={eyebrow}
        onCloseAutoFocus={(e) => {
          if (closedByScrollRef.current) e.preventDefault();
        }}
        // PopoverContent portals to document.body, outside .midas-v3-root:
        // re-wrap so the V3 tokens resolve (ComposerMenuV3 precedent).
        className={`midas-v3-root ${resolveV3PaletteClass()} cond-info__pop`}
        // Inline values override the primitive's z-50 / w-72 / p-1 /
        // bg-popover classes.
        style={{
          zIndex: COND_INFO_POPOVER_Z,
          width: "auto",
          maxWidth: `min(${COND_INFO_BOX_MAX_WIDTH}px, calc(100vw - 16px))`,
          padding: "12px 14px",
          background: "var(--ink)",
          color: "var(--card)",
          border: "none",
          borderRadius: 8,
          boxShadow: "var(--shadow)",
          fontFamily: "var(--font-sans)",
        }}
      >
        <SrcInfoBoxBodyV3 eyebrow={eyebrow} body={text} wrapAnywhere />
      </PopoverContent>
    </Popover>
  );
};

export default CondInfoV3;
